import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight, ChevronDown, Users, Coins, Smile } from "lucide-react";
import { useLoans } from "../components/loans/LoansContext";
import { GradButton, GhostButton, GRAD_TEXT, LenderLogo } from "../components/loans/LoansUi";
import { bestForAmount, fmtLakh, rateParts } from "../lib/loanStats";

const AMOUNTS = [25000, 50000, 100000, 200000, 500000, 1000000, 1500000];
const TENURES = [3, 6, 12, 24, 36, 48, 60];

export function useSeo(title, desc) {
  useEffect(() => {
    const prev = document.title;
    document.title = title;
    let meta = document.querySelector('meta[name="description"]');
    if (!meta) { meta = document.createElement("meta"); meta.setAttribute("name", "description"); document.head.appendChild(meta); }
    const prevDesc = meta.getAttribute("content");
    meta.setAttribute("content", desc);
    return () => { document.title = prev; if (prevDesc !== null) meta.setAttribute("content", prevDesc); };
  }, [title, desc]);
}

export function Select({ value, onChange, children, testid }) {
  return (
    <div className="relative">
      <select data-testid={testid} value={value} onChange={(e) => onChange(e.target.value)}
        className="w-full h-14 appearance-none rounded-xl border border-slate-200 bg-white pl-4 pr-10 text-[15px] font-medium text-[#0B1B3F] outline-none focus:border-[#10B981] focus:ring-4 focus:ring-emerald-100 transition-shadow">
        {children}
      </select>
      <ChevronDown className="w-4 h-4 absolute right-4 top-1/2 -translate-y-1/2 pointer-events-none text-[#0B1B3F]" />
    </div>
  );
}

export function RateText({ rate, big = "text-2xl" }) {
  const { value, unit } = rateParts(rate);
  return (
    <span className="whitespace-nowrap">
      <span className={`${big} font-extrabold text-[#0FA37F]`}>{value}</span>
      {unit && <span className="text-xs font-semibold text-[#0FA37F] ml-1">{unit}</span>}
    </span>
  );
}

function EnquiryForm() {
  const { products, apply } = useLoans();
  const nav = useNavigate();
  const [amount, setAmount] = useState("");
  const [tenure, setTenure] = useState("");
  const go = () => apply(bestForAmount(products, Number(amount) || 100000));
  return (
    <div data-testid="le-enquiry-form" className="mt-10 bg-white rounded-3xl border border-slate-100 shadow-[0_20px_60px_-25px_rgba(15,40,90,0.25)] p-6 md:p-7 max-w-xl le-rise" style={{ animationDelay: "120ms" }}>
      <h2 className="text-xl font-bold">Get Your Personal Loan</h2>
      <div className="mt-5 grid sm:grid-cols-2 gap-4">
        <Select testid="le-home-amount" value={amount} onChange={setAmount}>
          <option value="">Loan Amount (₹)</option>
          {AMOUNTS.map((a) => <option key={a} value={a}>{fmtLakh(a)}</option>)}
        </Select>
        <Select testid="le-home-tenure" value={tenure} onChange={setTenure}>
          <option value="">Tenure</option>
          {TENURES.map((t) => <option key={t} value={t}>{t} Months</option>)}
        </Select>
      </div>
      <GradButton data-testid="le-home-apply" onClick={go} disabled={!products.length} className="mt-5 w-full h-12">Apply Now <ArrowRight className="w-4 h-4" /></GradButton>
      <GhostButton data-testid="le-home-eligibility" onClick={() => nav(`/check-eligibility${amount ? `?amount=${amount}` : ""}`)} className="mt-3 w-full h-12">Check Eligibility &amp; Rates</GhostButton>
    </div>
  );
}

function OffersList() {
  const { products, loaded, apply } = useLoans();
  const nav = useNavigate();
  return (
    <div className="le-rise" style={{ animationDelay: "200ms" }}>
      <h2 className="text-3xl font-extrabold tracking-tight">Popular Loan Offers</h2>
      <p className="mt-2 text-[#4B5B7C]">Personal loan deals from our trusted lending partners</p>
      <div className="mt-6 space-y-3">
        {loaded && !products.length && <div className="rounded-2xl border border-dashed border-slate-200 p-8 text-center text-sm text-[#4B5B7C]">No lenders live yet.</div>}
        {products.slice(0, 4).map((p) => (
          <button key={p.id} data-testid={`le-offer-${p.id}`} onClick={() => apply(p)}
            className="w-full text-left bg-white rounded-2xl border border-slate-100 shadow-sm hover:shadow-lg hover:-translate-y-0.5 transition-[box-shadow,transform] px-5 py-4 flex items-center gap-5 group">
            <LenderLogo p={p} size="w-16 h-12" />
            <div className="flex-1 min-w-0">
              <div className="font-bold truncate">{p.provider}</div>
              <div className="text-sm text-[#4B5B7C]">Personal Loan</div>
            </div>
            <RateText rate={p.interest_rate} />
            <ArrowRight className="w-5 h-5 text-[#0A5BFF] group-hover:translate-x-1 transition-transform" />
          </button>
        ))}
      </div>
      <div className="mt-6 flex justify-center">
        <GhostButton data-testid="le-view-all-offers" onClick={() => nav("/lenders")} className="h-12 px-10 rounded-full">View All Loan Offers <ArrowRight className="w-4 h-4" /></GhostButton>
      </div>
    </div>
  );
}

function TrustBar() {
  const { products } = useLoans();
  const total = useMemo(() => Math.max(0, ...products.map((p) => p.loan_max || 0)), [products]);
  const items = [
    { icon: Users, top: `${products.length || "—"}`, bottom: "Trusted Lenders" },
    { icon: Coins, top: fmtLakh(total), bottom: "Max Loan Amount" },
    { icon: Smile, top: "100%", bottom: "Digital Process" },
  ];
  return (
    <div data-testid="le-trust-bar" className="mt-16 grid grid-cols-1 sm:grid-cols-3 gap-6 sm:divide-x divide-slate-200">
      {items.map((it) => (
        <div key={it.bottom} className="flex items-center justify-center gap-4">
          <it.icon className="w-10 h-10 text-[#0FA3A0]" strokeWidth={1.5} />
          <div><div className="text-2xl font-extrabold">{it.top}</div><div className="text-[#4B5B7C]">{it.bottom}</div></div>
        </div>
      ))}
    </div>
  );
}

export default function LoansLanding() {
  useSeo("Get Best Personal Loan Deals · loanexpress", "Compare rates, check eligibility and apply for personal loans from trusted RBI-registered lenders on loanexpress by Offer Express.");
  return (
    <section className="max-w-7xl mx-auto px-5 md:px-10 pt-12 md:pt-16 pb-20">
      <div className="grid grid-cols-1 lg:grid-cols-[1.05fr_1fr] gap-14 items-start">
        <div className="min-w-0">
          <span className="inline-block rounded-full bg-gradient-to-r from-[#0A5BFF] to-[#10B981] text-white text-xs font-semibold px-4 py-2 le-rise">Personal Loan Marketplace in India</span>
          <h1 className="mt-6 text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.05] le-rise">
            Get Best <span className={GRAD_TEXT}>Personal Loan</span> Deals from Top Banks &amp; NBFCs
          </h1>
          <p className="mt-5 text-base md:text-lg text-[#4B5B7C] max-w-xl le-rise">
            Compare rates, check eligibility, and apply for personal loans from trusted lenders. Quick approval, minimal documentation, and competitive interest rates.
          </p>
          <EnquiryForm />
        </div>
        <div className="lg:pt-16 min-w-0"><OffersList /></div>
      </div>
      <TrustBar />
    </section>
  );
}
